export const FTM_2019_ARTICLES = [
  {
    slug: 'troubleshooting-family-tree-maker-2019',
    title: 'FTM 2019 Not Working Right? A Calm, Step-by-Step Repair Guide',
    excerpt:
      'Crashes, frozen screens, a tree that crawls, or strange error boxes? Here is the order we suggest for getting Family Tree Maker 2019 back on its feet.',
    intro: [
      'When Family Tree Maker 2019 starts acting up, it is natural to worry about years of research. Take a breath. In the vast majority of cases your people, dates, and photos are perfectly safe, and the program is reacting to something around it: a recent computer update, a sync that got cut off halfway, or a tree that has simply grown very big.',
      'This guide moves from the gentlest fixes to the more involved ones. Try each step, test the program, and only move on if the problem is still there. That way you change as little as possible and can tell exactly which step made the difference.',
    ],
    sections: [
      {
        heading: 'Why Family Tree Maker 2019 misbehaves',
        paragraphs: [
          'Family Tree Maker keeps your tree in a database that is read and rewritten constantly while you work. Anything that interrupts that process can leave small inconsistencies behind. Typical triggers include the computer going to sleep with the program open, a power cut, security software locking the file mid-save, or an Ancestry sync that was closed before it completed.',
          'Operating system upgrades are the other big cause. A major Windows or macOS release can change permissions, drivers, or system components the program relies on, and an older build of Family Tree Maker may stumble over those changes until it is patched.',
        ],
      },
      {
        heading: 'Protect your research first',
        paragraphs: [
          'Before touching any settings, make a safety copy. With the tree open, go to the File menu and choose Backup. Save the copy somewhere separate, like a flash drive or a cloud storage folder, and give it a name that includes today\'s date. Think of it as an insurance policy: whatever happens next, you can return to this exact point.',
        ],
      },
      {
        heading: 'Five simple things to try first',
        steps: [
          'Close Family Tree Maker, confirm it is not still running in the background, and restart your computer.',
          'Use the Help menu to see whether a newer update for your edition is available, and install it if so.',
          'Run your operating system\'s own updates (Windows Update on a PC, Software Update on a Mac), then restart one more time.',
          'Ask your antivirus program to skip the Family Tree Maker folder for a short test, then open the tree again. Turn full protection back on afterward.',
          'Try another tree, or make a quick test tree with two or three people. If the test tree runs smoothly, the program itself is fine and the issue sits inside your main tree file.',
        ],
      },
      {
        heading: 'When only one tree is the problem',
        paragraphs: [
          'Family Tree Maker includes a maintenance tool called Compact File, found under the File menu. It reorganizes the database behind the scenes and rebuilds its internal indexes. People are often surprised how much it helps with sluggish searching, empty panels, and crashes that seem to come from nowhere. For trees with many thousands of people, running it twice in a row can help further.',
          'If the tree still misbehaves, bring back your most recent healthy backup, but give the restored copy a fresh name so it does not overwrite anything. Open both versions and compare them side by side before you decide which one to keep.',
        ],
      },
      {
        heading: 'Matching the symptom to the likely cause',
        bullets: [
          'Closes right after the splash screen: often an outdated program build or damaged settings files.',
          'Opens, but relatives seem to have disappeared: frequently the result of a sync or restore that stopped partway.',
          'Slow to type, scroll, or switch workspaces: usually a very large media collection or a file that has not been compacted in a long time.',
          'Charts or reports will not print or save as PDF: look at your printer driver and default printer first.',
          'Error messages while saving: stop editing, save a separately named backup, and compact before continuing.',
        ],
      },
      {
        heading: 'Common mistakes that make things worse',
        bullets: [
          'Deleting and re-downloading a synced tree before making a local backup, which can lose private notes and unsynced edits.',
          'Reinstalling the program again and again without trying updates and Compact File first.',
          'Copying tree files by hand while Family Tree Maker is still open.',
          'Moving the tree into a cloud-synced folder to "keep it safe," which can actually lead to file damage.',
        ],
      },
      {
        heading: 'If nothing on this list helps',
        paragraphs: [
          'Write down exactly what you see: the wording of any error message, what you clicked just before, and whether it happens with every tree or only one. A screenshot is even better. Those details make it far quicker for anyone helping you, whether a friend, a user forum, or a support technician, to pinpoint the cause.',
          'At that stage a clean reinstall may be worth trying. Uninstalling Family Tree Maker normally leaves your tree files where they are, but keep your dated backup handy just in case.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is Compact File safe to run on my tree?',
        a: 'Yes. It only reorganizes how the information is stored so the file runs more smoothly. Nothing about your people, citations, or pictures is thrown away. A backup beforehand is still sensible, simply as good practice.',
      },
      {
        q: 'Should I reinstall the program right away?',
        a: 'Usually not. Reinstalling rarely fixes a problem that lives inside a tree file. Work through restarts, updates, and Compact File before that. Should you reinstall later, your trees normally stay put in your Documents folder.',
      },
      {
        q: 'Could a Windows or Mac update really cause these problems?',
        a: 'It can. Big system updates sometimes change how programs are allowed to reach files or hardware. Installing the latest available update for Family Tree Maker and restarting often clears it up.',
      },
      {
        q: 'My tree is huge. Is that why it feels slow?',
        a: 'Size plays a part, especially with thousands of photos attached. Regular compacting, keeping media on the internal drive, and closing other heavy programs while you work all help a large tree feel quicker.',
      },
    ],
  },
  {
    slug: 'transfer-ftm-2019-to-new-computer',
    title: 'Setting Up FTM 2019 on a Replacement Computer Without Losing Photos',
    excerpt:
      'A practical walkthrough for bringing your Family Tree Maker 2019 research, pictures, and documents across to a new Windows PC or Mac.',
    intro: [
      'Getting a new computer should feel like a fresh start, not a scramble to find where all your family photos went. The most common complaint after a move is a tree full of empty picture frames, and it nearly always comes from copying the tree file by itself and leaving the media behind.',
      'The safer route is to let Family Tree Maker package everything for you. A backup made with media bundles your tree and its attached files into one file you can carry across. Set aside about half an hour, have a flash drive or cloud account ready, and follow along below.',
    ],
    sections: [
      {
        heading: 'Before you begin: gather these items',
        bullets: [
          'Your Family Tree Maker serial number, or the email address linked to your purchase.',
          'Your Ancestry sign-in details if your tree is synced.',
          'A flash drive with plenty of free space, or a cloud folder both computers can reach.',
          'The old computer, still working and switched on, in case something needs a second look.',
        ],
      },
      {
        heading: 'Packing up the tree on your old computer',
        steps: [
          'Start Family Tree Maker and load the tree you want to move.',
          'Click the File menu, then Backup.',
          'When the backup options appear, make sure media files are part of it. This is the single most important choice in the whole process.',
          'Save the backup to your flash drive or cloud folder, and jot down its name.',
          'If you keep more than one tree, repeat these steps for each of them.',
        ],
      },
      {
        heading: 'Unpacking the tree on your new computer',
        steps: [
          'Put Family Tree Maker on the new machine, whether that is the 2019 edition or a later one you upgraded to, and activate it with your serial number.',
          'Open the File menu, pick Restore, and locate the backup you brought across.',
          'Choose where the restored tree should live. The standard Family Tree Maker folder inside Documents is a sensible spot.',
          'Wait patiently until the program says the restore is done. Large trees with many photos can take a while.',
          'Visit the Media workspace and scroll through a few items to be sure the images appear.',
        ],
      },
      {
        heading: 'Moving from Windows to Mac, or the other way around',
        paragraphs: [
          'Family Tree Maker backups can be restored on either platform, so the process is the same. The catch is that Windows and macOS describe file locations differently. If a handful of pictures show as missing after the restore, open the Media workspace, run Find Missing Media, and direct it to the folder where the restored media ended up.',
          'Menus look slightly different too. On a Mac the File menu sits in the bar along the very top of the screen, while on Windows it appears inside the program window itself.',
        ],
      },
      {
        heading: 'A note for trees linked to Ancestry',
        paragraphs: [
          'If your tree uses FamilySync, you could download it again from Ancestry on the new machine. Even so, we suggest restoring your own backup first. Some things, such as program preferences, private details, and media you never uploaded, exist only on your computer. After restoring, sign in and check the sync status to confirm the connection to your online tree is still in place.',
        ],
      },
      {
        heading: 'Common mistakes during a move',
        bullets: [
          'Copying only the tree file and forgetting the media folder.',
          'Using a backup that was created without the media option switched on.',
          'Downloading the synced tree and also restoring a backup, ending up with two copies that both try to sync.',
          'Wiping or recycling the old computer before checking everything on the new one.',
        ],
      },
      {
        heading: 'If the restore fails or looks incomplete',
        paragraphs: [
          'First, copy the backup onto the new computer\'s internal drive and restore from there instead of straight from a flash drive or cloud folder. Also check that the new computer has enough free disk space. If it still fails, return to the old computer, run Compact File on the tree, make a fresh backup with media, and try once more.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Can I activate Family Tree Maker on my new computer with the same serial number?',
        a: 'In most cases, yes. Licenses are generally meant to cover more than one computer that you personally own. If activation is refused because your installs are used up, the software publisher\'s customer service can usually reset them.',
      },
      {
        q: 'When is it safe to remove the program from my old computer?',
        a: 'Wait until the new computer shows your full tree, your photos, and, if you use it, a working Ancestry sync. Once you are happy with all three, go ahead and uninstall it from the old machine.',
      },
      {
        q: 'Can I just copy my Documents folder across instead?',
        a: 'You can, but it is easy to miss files or break picture links that way. A backup with media is simpler and more reliable, and it keeps everything in one tidy package.',
      },
      {
        q: 'How big will my backup file be?',
        a: 'That depends almost entirely on your photos and documents. A tree with few pictures may be small, while one with thousands of scanned images can run to several gigabytes, so choose a drive with generous space.',
      },
    ],
  },
  {
    slug: 'fixing-broken-media-links-ftm-2019',
    title: 'Photos Missing in FTM 2019? How to Reconnect Your Media',
    excerpt:
      'Blank picture frames in your tree usually mean the files moved, not that they vanished. Here is how to point Family Tree Maker 2019 back to them.',
    intro: [
      'Few things are as alarming as opening your tree and finding treasured photos of your grandparents replaced by empty placeholders. The reassuring part is that the images themselves are almost always still sitting on your computer or an attached drive.',
      'Family Tree Maker does not tuck your pictures inside the tree. Instead, it remembers the exact spot where each file is stored. If a file is moved, renamed, or ends up on a disconnected drive, that saved address leads nowhere. Reconnecting simply means showing the program the new location.',
    ],
    sections: [
      {
        heading: 'How picture links usually break',
        bullets: [
          'Pictures were sorted into new folders or renamed using File Explorer on Windows or Finder on a Mac.',
          'The external hard drive holding your scans is unplugged, or Windows gave it a different drive letter.',
          'The tree came from a backup that was created without its media.',
          'A cloud service such as OneDrive, iCloud Drive, or Dropbox freed up space by leaving only online placeholders on your computer.',
          'Files were accidentally deleted during a computer clean-up.',
        ],
      },
      {
        heading: 'Using Find Missing Media, one step at a time',
        steps: [
          'Switch to the Media workspace.',
          'Open the Media menu and select Find Missing Media.',
          'Either let Family Tree Maker hunt for the files automatically or point it yourself to wherever your pictures are stored today.',
          'Look over the files it proposes and confirm the matches that are correct.',
          'Head back to the People workspace and check a few relatives to make sure their photos have returned.',
        ],
      },
      {
        heading: 'Why fixing one link fixes it everywhere',
        paragraphs: [
          'Each picture is stored once in your media collection, even if it is attached to several people, events, or sources. When you reconnect it, every place that uses that picture updates at the same moment, so there is no need to go through your tree person by person.',
        ],
      },
      {
        heading: 'Tips for a large photo collection',
        bullets: [
          'Plug in every drive that might hold your pictures before starting the search.',
          'Search one main folder at a time rather than the entire computer, which keeps things fast.',
          'If cloud storage is involved, let it finish downloading the files before you run the search.',
          'Work in batches, and run Compact File once you are finished.',
        ],
      },
      {
        heading: 'Keeping your pictures connected from now on',
        paragraphs: [
          'Choose one home for all your genealogy media, ideally a folder inside the Family Tree Maker folder in Documents, and leave it there. If you want to rename or reorganize pictures, do it from inside Family Tree Maker rather than through your computer\'s file browser, so the program can keep track.',
          'If that folder is covered by a cloud service, change its setting so files are always kept on this device. That stops the service from quietly swapping your photos for online-only shortcuts.',
        ],
      },
      {
        heading: 'Mac and Windows differences',
        paragraphs: [
          'The steps are identical on both systems. The main difference shows up after moving a tree from one platform to the other: folder paths are written differently, so many links can break all at once. Running Find Missing Media and directing it to the folder that holds your restored pictures usually reconnects them in a single pass.',
        ],
      },
    ],
    faqs: [
      {
        q: 'After the search, some pictures are still missing. What should I do?',
        a: 'Those particular pictures might have been removed or given a different name. Try searching your computer for the original file name, look in the Recycle Bin or Trash, or restore an earlier backup that was made with media and copy the pictures out of it.',
      },
      {
        q: 'Will reconnecting media change my photos in any way?',
        a: 'No. The tool only updates where Family Tree Maker looks for each file. The pictures themselves are not edited, resized, or moved.',
      },
      {
        q: 'Do photos from my Ancestry tree behave the same way?',
        a: 'Media that has synced can often be downloaded again from your online tree, but pictures you added only on your computer depend on local files. Keeping those files in one steady location matters most.',
      },
      {
        q: 'Can I prevent this when I buy a new computer?',
        a: 'Yes. Move your tree using a backup that includes media rather than copying files by hand. That keeps the pictures and the tree traveling together.',
      },
    ],
  },
  {
    slug: 'color-coding-features-ftm-2019',
    title: 'Organizing Branches with Colors in Family Tree Maker 2019',
    excerpt:
      'Learn how FTM 2019 color groups make each side of the family easy to spot, plus smart ways to use them for tracking your research.',
    intro: [
      'Once a tree grows past a few hundred people, it gets hard to remember which cousin belongs to which side of the family. Color coding solves that by letting you assign a color to one person, or to a whole group of ancestors or descendants, so each branch stands out when you browse your tree.',
      'There is no risk in trying it. A color is simply a visual marker. It does not add, remove, or change any relationships, facts, or sources, and you can take it off whenever you like.',
    ],
    sections: [
      {
        heading: 'What color coding is good for',
        paragraphs: [
          'Colors turn a sea of names into something your eye can sort quickly. Many researchers use them to separate the four grandparent lines, but they are just as handy for tracking progress, for example marking people you have finished researching or those you still need to double-check.',
        ],
      },
      {
        heading: 'Giving someone a color',
        steps: [
          'Go to the People workspace and click the person you want to start with.',
          'Find the color coding choice on the toolbar or in the Person menu.',
          'Decide how far the color should reach: just this person, everyone in their ancestry, or all their descendants.',
          'Select one of the color groups and confirm.',
          'Glance at the tree view to see the new colors in place.',
        ],
      },
      {
        heading: 'Popular ways to set up color groups',
        bullets: [
          'The classic setup: a separate color for each of your four grandparents, so every ancestor falls into a clear branch.',
          'A "fully sourced" color for people whose facts are backed by records.',
          'A "needs checking" color for DNA matches, educated guesses, or conflicting information.',
          'A color for immigrants, or for ancestors from a particular country or region.',
          'A color for the surname or line you are actively researching this month.',
        ],
      },
      {
        heading: 'Where your colors are saved',
        paragraphs: [
          'Colors are stored as part of your tree file. Because of that, a normal backup and restore brings them along. This is something only the desktop program offers, however, so colors generally do not carry over to your online Ancestry tree when you sync.',
        ],
      },
      {
        heading: 'Common mistakes to avoid',
        bullets: [
          'Using so many colors that nothing stands out anymore. Four to six groups is plenty for most trees.',
          'Forgetting what each color means. Keep a short note in your research log or on a sticky note by the screen.',
          'Coloring every descendant of a distant ancestor when you only meant to mark one line, which can paint a huge part of the tree.',
          'Expecting colors to show up on Ancestry or inside exported GEDCOM files.',
        ],
      },
      {
        heading: 'Tips for large trees',
        paragraphs: [
          'In a very big tree, start with direct ancestors only and add descendants later. Coloring huge groups at once can take a moment, so give the program time to finish before clicking anything else. If the display seems slow afterward, running Compact File from the File menu often helps.',
        ],
      },
    ],
    faqs: [
      {
        q: 'How do I remove a color I no longer want?',
        a: 'Click the person or color group, then pick the option to clear it. If you would rather start over, you can reset every member of a group together instead of one at a time.',
      },
      {
        q: 'Why does someone show a color I did not expect?',
        a: 'Branches sometimes overlap, for example when cousins married each other, so one person can fall inside more than one group you applied. Review which ancestor or descendant groups you set up and adjust from there.',
      },
      {
        q: 'Will colors appear on my printed charts?',
        a: 'Color coding is designed mainly for viewing on screen. Charts and reports have their own formatting settings, so check the print preview before you print.',
      },
      {
        q: 'Do colors change anything when I share my tree?',
        a: 'No. They are purely visual and stay within Family Tree Maker. The people and facts you share are unaffected.',
      },
    ],
  },
  {
    slug: 'manage-relationships-ftm-2019',
    title: 'Fixing Spouses, Parents, and Duplicates in FTM 2019',
    excerpt:
      'How to add partners, move a child to the right parents, label adoptive or step links, and clean up doubled-up people in Family Tree Maker 2019.',
    intro: [
      'Every name in your tree is tied to others through parents, partners, and children. When one of those ties is wrong, the error spreads into charts, reports, and relationship calculations. Family Tree Maker 2019 can handle complicated real-life families, including second marriages, blended households, and adoptions, but some of the tools are tucked away where they are easy to miss.',
      'This guide covers the fixes people ask about most. None of them require starting over, and in most cases you will not lose a single fact or photo along the way.',
    ],
    sections: [
      {
        heading: 'Why relationship errors creep in',
        bullets: [
          'Clicking the wrong person when adding a child or spouse, which is easy to do in a busy family view.',
          'Importing a GEDCOM file or bringing in someone else\'s research that followed different conventions.',
          'Syncing with an online tree where a relative made changes.',
          'Adding the same ancestor twice from two different record hints.',
        ],
      },
      {
        heading: 'Adding a husband, wife, or partner',
        steps: [
          'In the People workspace, click the person who needs a partner.',
          'Use the family view or the Person menu to add a spouse.',
          'If the person married more than once, mark one partner as preferred. Charts and the main family view will lead with that couple.',
          'Enter the wedding, divorce, or separation as a fact on the couple\'s relationship, so it belongs to both partners together.',
        ],
      },
      {
        heading: 'Moving a child to the correct parents',
        paragraphs: [
          'Resist the urge to delete someone who has been attached to the wrong family. Deleting removes all their facts, sources, and pictures. Instead, detach the person from the incorrect parents, then link them to the right couple. Their records stay intact through the whole process.',
        ],
      },
      {
        heading: 'Recording adoption, step, and foster links',
        paragraphs: [
          'Family Tree Maker lets you describe the nature of each bond between a parent and a child. It starts out treated as biological, but you can switch it to adopted, foster, step, or unknown. Getting this right means your charts and reports tell the true story of how the family came together, and it helps anyone who reads your research in years to come.',
        ],
      },
      {
        heading: 'Cleaning up duplicate people',
        paragraphs: [
          'Two records for the same ancestor are common after imports, syncs, or accepting hints in a hurry. The merge feature shows both versions next to each other so you can decide which name spellings, dates, and places to keep. Always make a backup first. Merging is meant to be permanent, and pulling two people apart again later is difficult.',
        ],
      },
      {
        heading: 'Quick checklist before you merge',
        bullets: [
          'Are the birth dates and places truly compatible, or just similar?',
          'Do both records list the same parents or spouse?',
          'Could they actually be a father and son, or two cousins, sharing a family name?',
          'Have you saved a dated backup in the last few minutes?',
        ],
      },
      {
        heading: 'Common mistakes to avoid',
        bullets: [
          'Deleting a person to fix a wrong link instead of detaching them.',
          'Entering a marriage date on only one spouse\'s personal facts.',
          'Merging on name alone. Families often reused the same first names across generations.',
          'Leaving every relationship set to biological when you know otherwise.',
        ],
      },
    ],
    faqs: [
      {
        q: 'I merged the wrong two people. Can I get them back?',
        a: 'The most dependable fix is to restore the safety copy you saved just before merging. If an undo option is offered right after the merge, you can try it, but a backup is the surest way back.',
      },
      {
        q: 'Why does my chart show the wrong spouse first?',
        a: 'Family Tree Maker leads with whichever spouse is marked as preferred. Select the person, review their partners, and set the correct one as preferred.',
      },
      {
        q: 'How do I show that a child\'s father is unknown?',
        a: 'You can leave the father blank, or set the relationship type to unknown where a link exists but the details are uncertain. Adding a note that explains what you do know helps future researchers.',
      },
      {
        q: 'Will fixing relationships affect my Ancestry tree?',
        a: 'If the tree is synced, your corrections will be sent to Ancestry during the next sync. Make changes carefully and let the sync finish before closing the program.',
      },
    ],
  },
  {
    slug: 'family-book-creator-ftm-2019',
    title: 'Turning Your FTM 2019 Research into a Printed Family Book',
    excerpt:
      'How to get your tree ready, build chapters, and produce a polished family history book with the Family Book Creator add-on in Family Tree Maker 2019.',
    intro: [
      'A printed family history is one of the most meaningful gifts you can pass down. The Family Book Creator plugin works with Family Tree Maker and gathers your people, stories, photos, charts, and sources into a book layout with chapters and an index.',
      'The finished book can only be as neat as the tree behind it. Spending an evening tidying names, dates, and photos before you start will save hours of corrections later and gives you a result you will be proud to share with relatives.',
    ],
    sections: [
      {
        heading: 'What the plugin can pull into a book',
        bullets: [
          'Narrative chapters covering the ancestors or descendants of a chosen person.',
          'Photos and captions from your media collection.',
          'Charts and reports produced from your tree.',
          'Source notes and an index of names.',
        ],
      },
      {
        heading: 'Tidying your tree first',
        bullets: [
          'Make name spellings and date styles consistent so the index looks professional.',
          'Flag anyone still living, plus any sensitive details, as private.',
          'Choose a main portrait for each important person.',
          'Attach sources to key facts such as births, marriages, and deaths.',
          'Correct obvious typos in place names, which tend to jump out in print.',
        ],
      },
      {
        heading: 'Building the book',
        steps: [
          'Install Family Book Creator, then restart Family Tree Maker. The plugin usually appears under a Plugins menu.',
          'Pick the person the book will center on and decide whether it traces the people before them or the generations after them.',
          'Tick the chapters, charts, and reports that belong in the book.',
          'Preview the layout and scroll through every section.',
          'Send the result to PDF or a word processor to add introductions, family stories, and final touches.',
        ],
      },
      {
        heading: 'Getting a good print',
        paragraphs: [
          'Always create a PDF and read it through on screen before printing a single copy. Check page breaks, photo quality, and the index. Books packed with pictures generally come out much better from a professional or online print service than from a home printer, especially if you want a bound copy.',
        ],
      },
      {
        heading: 'Tips for a large family book',
        bullets: [
          'Limit the number of generations for your first draft, then expand once you like the layout.',
          'Split a very big book into volumes by branch, such as one per grandparent line.',
          'Use good quality scans, since low-resolution images look blurry on paper.',
          'Keep a list of the options you chose so you can rebuild the book the same way after correcting the tree.',
        ],
      },
      {
        heading: 'If the plugin will not start',
        paragraphs: [
          'Make sure the plugin version matches your Family Tree Maker edition, since add-ons made for one version may not load in another. Reinstall it, restart the computer, and open Family Tree Maker again. If a large tree makes the plugin slow or unresponsive, try running Compact File first and building a smaller test book.',
        ],
      },
    ],
    faqs: [
      {
        q: 'There is nothing under the Plugins menu. Why?',
        a: 'Most often the plugin is not installed for your particular edition of Family Tree Maker, or it is out of date. Reinstall the matching version and restart the program.',
      },
      {
        q: 'Is Family Book Creator part of Family Tree Maker itself?',
        a: 'It is an add-on that works alongside Family Tree Maker rather than a built-in feature. Check the plugin\'s own documentation for version and licensing details.',
      },
      {
        q: 'Will private people appear in the book?',
        a: 'Details you have marked private are intended to stay out of shared output, but always look through the preview to be certain before printing or handing out copies.',
      },
      {
        q: 'Can I edit the text after the book is built?',
        a: 'Yes. Sending the book to a word processor lets you rewrite passages, add stories, and adjust the layout before you print.',
      },
    ],
  },
  {
    slug: 'family-tree-maker-multiple-computers',
    title: 'One Tree, Two Computers: Keeping a Desktop and Laptop in Step',
    excerpt:
      'Keep a single Family Tree Maker tree up to date across two computers without doubled-up people, clashing edits, or lost research.',
    intro: [
      'Many family historians do their main research at a desk at home but take a laptop to libraries, courthouses, cemeteries, or family reunions. It is perfectly possible to keep editing one tree from both machines. The secret is discipline: at any moment, only one computer should hold the copy you are actively changing.',
      'Below are two dependable methods, plus the habits that keep them from going wrong. Pick whichever one fits the way you like to work.',
    ],
    sections: [
      {
        heading: 'Why two copies cause trouble',
        paragraphs: [
          'A Family Tree Maker tree is a database, not a simple document. If you edit two separate copies, each one drifts in its own direction, and there is no easy way to blend them back together afterward. That is how people end up with duplicates, missing edits, or two slightly different versions of the same family.',
        ],
      },
      {
        heading: 'Approach A: let Ancestry carry the changes',
        paragraphs: [
          'If you use FamilySync, upload your tree to Ancestry using your main computer, then download that synced tree onto the second one. The golden rule is to sync when you finish on one computer and sync again when you sit down at the other. That way both copies always pick up the latest changes from your online tree.',
        ],
      },
      {
        heading: 'Approach B: carry a backup between computers',
        steps: [
          'When you wrap up a session on the first computer, make a backup with media included.',
          'Copy that backup to a flash drive or cloud folder.',
          'On the second computer, restore it and do your work there.',
          'At the end of that session, make a new backup and restore it on the first computer.',
          'Treat whichever computer has the newest restore as the only one you edit.',
        ],
      },
      {
        heading: 'Comparing the two approaches',
        bullets: [
          'Syncing is convenient and quick, but it needs an internet connection and an Ancestry account.',
          'Backup and restore works entirely offline and brings along all local media and settings.',
          'Some researchers combine the two, syncing day to day and making a full backup every week.',
        ],
      },
      {
        heading: 'Mistakes that cause conflicts',
        bullets: [
          'Having both computers open one tree file stored on a network drive or cloud folder.',
          'Working offline on both machines and planning to sort it out later.',
          'Collecting copies with names like "Final," "Final new," and "Final really."',
          'Forgetting to sync before shutting the laptop lid.',
        ],
      },
      {
        heading: 'Quick end-of-session checklist',
        bullets: [
          'Has the sync finished, or has the backup been saved?',
          'Did you close Family Tree Maker properly before shutting down?',
          'Does the backup file name include today\'s date?',
          'Do you know which computer holds the newest copy?',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is it safe to store my working tree inside Dropbox or OneDrive?',
        a: 'We advise against it. Cloud services copy files while you are using them, which can damage a database that is open. Put your backups in the cloud, but keep the working tree on your computer\'s internal drive.',
      },
      {
        q: 'What if I accidentally edited both copies?',
        a: 'Decide which copy has the more important changes and keep that as your main tree. Then re-enter the smaller set of changes by hand. Merging whole trees together often creates duplicates.',
      },
      {
        q: 'Do I need two licenses for two computers?',
        a: 'Many Family Tree Maker licenses let you install the program on several computers that belong to you. Check your license terms, and contact the publisher if activation is refused.',
      },
      {
        q: 'Will my photos follow me between computers?',
        a: 'Media you have synced to Ancestry can usually come down on the second computer, but local-only files will not. The backup method carries all your media as long as you include it.',
      },
    ],
  },
  {
    slug: 'avoid-ftm-2019-data-corruption',
    title: 'Keeping Your FTM 2019 Tree Healthy and Free from Damage',
    excerpt:
      'Straightforward routines that help prevent damaged tree files, lost edits, and unexpected crashes in Family Tree Maker 2019.',
    intro: [
      'Serious file damage in Family Tree Maker is not common, but it is upsetting when it does happen, especially if the last backup was months ago. From what we see, most damaged trees trace back to a small handful of avoidable situations, such as a laptop dozing off mid-save or a tree file kept on a flash drive.',
      'A few easy routines lower that risk dramatically. None of them take more than a minute or two, and together they mean that even if something does go wrong, you lose very little.',
    ],
    sections: [
      {
        heading: 'What actually damages a tree file',
        bullets: [
          'Shutting down, restarting, or letting the computer sleep while Family Tree Maker is still open.',
          'Power cuts, or a laptop battery running flat in the middle of a session.',
          'Closing the program while an Ancestry sync is still running.',
          'Keeping the working tree on removable drives, network storage, or a cloud-synced folder.',
          'Security software locking the file while it is being saved.',
        ],
      },
      {
        heading: 'Good everyday habits',
        bullets: [
          'Exit Family Tree Maker yourself before turning off the computer or closing the laptop lid.',
          'Wait for syncs to show as complete before exiting.',
          'Store the working tree on the computer\'s internal drive.',
          'Install updates for the program and for Windows or macOS when they are offered.',
          'Plug laptops in during long research sessions.',
        ],
      },
      {
        heading: 'A simple backup routine',
        steps: [
          'At the close of each session, open the File menu and pick Backup.',
          'Put the date in the file name so you can tell backups apart.',
          'Hang on to your three newest backups and clear out older ones to save space.',
          'Store at least one copy away from the computer, on a flash drive or in cloud storage.',
          'Every few months, restore a backup under a test name to be sure it opens properly.',
        ],
      },
      {
        heading: 'Early warning signs',
        paragraphs: [
          'Pay attention if the program crashes every time you open a certain person, if people seem to disappear when the tree loads, or if error messages pop up as changes are saved. At the first sign, stop adding information. Save a separate backup with a different name, then run Compact File from the File menu. If the problems continue, go back to your most recent healthy backup.',
        ],
      },
      {
        heading: 'Routine maintenance for large trees',
        paragraphs: [
          'Big trees benefit from a little regular care. Running Compact File roughly once a month keeps the database tidy and responsive. It is also worth running Find Missing Media now and then to catch broken picture links, and cleaning up duplicate people, since both can make a large tree slower to work with.',
        ],
      },
      {
        heading: 'What to do if your tree is already damaged',
        steps: [
          'Stop editing and close Family Tree Maker.',
          'Copy the damaged tree file somewhere safe, so you still have it no matter what.',
          'Reopen the tree and try Compact File.',
          'If that does not work, restore your newest backup, give it a different name, and check it carefully.',
          'If the tree is synced with Ancestry, a fresh download of the online tree may be another way back, though local-only media and notes may need restoring separately.',
        ],
      },
    ],
    faqs: [
      {
        q: 'How often should I compact my tree?',
        a: 'Once a month is a good rhythm for large or busy trees, as well as after any big import or merge session. It is safe to run and helps stop small problems from building up.',
      },
      {
        q: 'Can I rely on my Ancestry sync as my only backup?',
        a: 'It helps, but it should not be your only safety net. Some information, including certain media and settings, lives only on your computer. Local backups with media fill that gap.',
      },
      {
        q: 'Can antivirus software harm my tree?',
        a: 'It rarely causes harm directly, but scanning a file while it is being saved can interrupt the save. If you notice problems, ask your security software to skip the Family Tree Maker folder.',
      },
      {
        q: 'Where should I keep my backups?',
        a: 'Keep one on your computer for convenience and at least one somewhere else, such as a flash drive stored in a safe place or a cloud storage account.',
      },
    ],
  },
];
